/**
 * DataFlowSession — the data-driven question flow's state.
 *
 * Holds raw customer answers (question code → selections/numbers), pins one
 * snapshot for the session, and refreshes the running scope list via the
 * server-resolving preview after each settled answer.
 *
 * The client never resolves triggers — it sends raw selections and the server
 * maps them (session vars, DERIVED, tile tier, unsure branches). This keeps the
 * scoping logic entirely server-side.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { pinSnapshot, previewAnswers, saveSession, ApiError, type RawAnswer } from './api';
import type { CustomerEstimateItem } from './types';

interface DataFlowValue {
  scopeItems: CustomerEstimateItem[];
  previewing: boolean;
  answers: Record<string, RawAnswer>;
  serviceLine: string;
  productCode: string;
  /** Record/replace an answer and refresh the scope list. */
  setAnswer: (answer: RawAnswer) => void;
  getAnswer: (questionCode: string) => RawAnswer | undefined;
  /** The pinned snapshot id for the session (pins on first use). */
  ensureSnapshot: () => Promise<string>;
  /** All answers as a list, for the final create call. */
  allAnswers: () => RawAnswer[];
  /** Autosave the session at the given screen index (debounced). */
  autosave: (currentScreenIndex: number) => void;
}

const Ctx = createContext<DataFlowValue | null>(null);

export function DataFlowProvider({
  serviceLine,
  productCode,
  initialAnswers,
  projectId,
  roomIndex,
  children,
}: {
  serviceLine: string;
  productCode: string;
  /** Answers restored from a saved session (resume). */
  initialAnswers?: RawAnswer[];
  /** Draft key extras when this flow is one room of a project loop. */
  projectId?: string;
  roomIndex?: number;
  children: ReactNode;
}): JSX.Element {
  const seed: Record<string, RawAnswer> = {};
  for (const a of initialAnswers ?? []) seed[a.questionCode] = a;

  const answersRef = useRef<Record<string, RawAnswer>>(seed);
  const snapshotIdRef = useRef<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [answers, setAnswers] = useState<Record<string, RawAnswer>>(seed);
  const [scopeItems, setScopeItems] = useState<CustomerEstimateItem[]>([]);
  const [previewing, setPreviewing] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setPreviewing(true);
    try {
      if (!snapshotIdRef.current) snapshotIdRef.current = await pinSnapshot();
      const list = Object.values(answersRef.current);
      const est = await previewAnswers({
        snapshotId: snapshotIdRef.current,
        serviceLine,
        productCode,
        answers: list,
      });
      setScopeItems(est.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        try {
          snapshotIdRef.current = await pinSnapshot();
          const est = await previewAnswers({
            snapshotId: snapshotIdRef.current,
            serviceLine,
            productCode,
            answers: Object.values(answersRef.current),
          });
          setScopeItems(est.items);
        } catch {
          /* keep last scope */
        }
      }
      // Other errors: keep the last known scope.
    } finally {
      setPreviewing(false);
    }
  }, [serviceLine, productCode]);

  const setAnswer = useCallback(
    (answer: RawAnswer): void => {
      answersRef.current[answer.questionCode] = answer;
      setAnswers({ ...answersRef.current });
      void refresh();
    },
    [refresh],
  );

  const getAnswer = useCallback((questionCode: string): RawAnswer | undefined => {
    return answersRef.current[questionCode];
  }, []);

  const ensureSnapshot = useCallback(async (): Promise<string> => {
    if (!snapshotIdRef.current) snapshotIdRef.current = await pinSnapshot();
    return snapshotIdRef.current;
  }, []);

  const allAnswers = useCallback((): RawAnswer[] => Object.values(answersRef.current), []);

  // Debounced autosave — persists raw answers + screen position. Failures are
  // swallowed (autosave is best-effort; the customer can still finish).
  const autosave = useCallback(
    (currentScreenIndex: number): void => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        void saveSession({
          serviceLine,
          productCode,
          answers: Object.values(answersRef.current),
          currentScreenIndex,
          ...(projectId ? { projectId } : {}),
          ...(roomIndex != null ? { roomIndex } : {}),
        }).catch(() => {
          /* best-effort */
        });
      }, 800);
    },
    [serviceLine, productCode, projectId, roomIndex],
  );

  const value = useMemo<DataFlowValue>(
    () => ({ scopeItems, previewing, answers, serviceLine, productCode, setAnswer, getAnswer, ensureSnapshot, allAnswers, autosave }),
    [scopeItems, previewing, answers, serviceLine, productCode, setAnswer, getAnswer, ensureSnapshot, allAnswers, autosave],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDataFlow(): DataFlowValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDataFlow must be used within DataFlowProvider');
  return ctx;
}
